import { cva, type VariantProps } from 'class-variance-authority'
import { BotIcon, CircleHelpIcon, CodeIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import type { UsageType } from './billing.interface'

const billingUsageTypeBadgeVariants = cva(
  'billing-usage-type-badge inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
  {
    variants: {
      type: {
        llm: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
        tool: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
        other: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300',
      },
    },
    defaultVariants: {
      type: 'llm',
    },
  }
)

export interface BillingUsageTypeBadgeProps
  extends VariantProps<typeof billingUsageTypeBadgeVariants> {
  type: UsageType
  className?: string
}

const TypeIcon: React.FC<{ type: UsageType }> = ({ type }) => {
  const iconProps = { className: 'size-3.5' }

  const iconMap: Record<UsageType, ReactNode> = {
    llm: <BotIcon {...iconProps} />,
    tool: <CodeIcon {...iconProps} />,
    other: <CircleHelpIcon {...iconProps} />,
  }

  return iconMap[type]
}

const typeLabelMap: Record<UsageType, string> = {
  llm: 'LLM',
  tool: 'Tool',
  other: 'Other',
}

const BillingUsageTypeBadgeBase: React.FC<BillingUsageTypeBadgeProps> = ({
  type,
  className,
}): ReactNode => (
  <span className={cn(billingUsageTypeBadgeVariants({ type }), className)}>
    <TypeIcon type={type} />
    {typeLabelMap[type]}
  </span>
)

export const BillingUsageTypeBadge = memo(BillingUsageTypeBadgeBase)

BillingUsageTypeBadge.displayName = 'BillingUsageTypeBadge'

export { billingUsageTypeBadgeVariants }
